import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';
import { useProfile } from '../contexts/ProfileContext';
import { usePlanCapabilities } from '../hooks/usePlanCapabilities';
import { getVisibleNavSections } from '../config/navConfig';
import { 
  getSidebarItems, 
  isItemEnabled, 
  groupItems, 
  GROUP_TITLES, 
  GROUP_TITLES_EN,
  GROUP_ICONS,
  COLLAPSIBLE_GROUPS,
  GROUP_ORDER,
  resolveRoute,
  getLabelForLang
} from '../navigation/sidebarConfig';
import { FEATURE_FLAGS } from '../navigation/featureFlags';
import { ICONS } from './navigation/icons';
import { isActiveRoute } from './navigation/isActive';
import SportSelector from './navigation/SportSelector';
import RoleSwitcher from './navigation/RoleSwitcher';
import SuperModeToggle from './navigation/SuperModeToggle';
import TeamAthleteSelector from './navigation/TeamAthleteSelector';
import AccountMenu from './navigation/AccountMenu';
import { useSport } from '../contexts/SportContext';
import { useSportsLang } from '../contexts/SportsLangContext';
import { useGovernanceLang } from '../contexts/GovernanceLangContext';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';


// *** Cores da marca ***

const GlobalSidebar = ({ isOpen = true, onClose = () => {} }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { activeRole, roles: userRoles } = useRBAC();
  const { activeSport, sports } = useSport();
  const { isSuperAdmin, superMode, hasCapability } = useProfile();
  const { lang: sportsLang, setLang: setSportsLang } = useSportsLang();
  const { lang: govLang, setLang: setGovLang } = useGovernanceLang();

  // Unified lang: governance pages use govLang, sports pages use sportsLang.
  // The toggle button syncs both so the sidebar is always consistent.
  const lang = sportsLang; // sidebar uses sports lang as primary display lang

  const setLang = (l) => {
    setSportsLang(l);
    setGovLang(l);
  };

  // Estado para secoes colapsaveis (ex: Gestao)
  const [collapsedSections, setCollapsedSections] = useState({});

  // Estado para ocultar seletores (perfil, esporte, visão)
  const [selectorsHidden, setSelectorsHidden] = useState(() => {
    try { return localStorage.getItem('arenastats_selectors_hidden') === 'true'; } catch { return false; }
  });
  const toggleSelectors = () => {
    setSelectorsHidden(prev => {
      const next = !prev;
      try { localStorage.setItem('arenastats_selectors_hidden', String(next)); } catch {}
      return next;
    });
  };

  // Use centralized admin check from ProfileContext
  const isAdmin = isSuperAdmin;

  // Selectors (sport, profile, club, athlete) are admin-only tools.
  // Regular users (athlete, coach, etc.) never see them — their context is
  // locked to their own profile/club/sport.
  const showSelectorsSection = isSuperAdmin;

  console.log('[GlobalSidebar] 🔍 Admin Debug:', {
    userRole: user?.role,
    userEmail: user?.email,
    isSuperAdmin,
    superMode,
    finalIsAdmin: isAdmin
  });

  // NEW: Usar sidebarConfig.js ao invés de navConfig.js
  const { hasPlanCapability } = usePlanCapabilities();
  const sidebarItems = getSidebarItems({
    role: activeRole,
    userRoles,              // ← token roles: sidebar never shows what token can't reach
    sport: activeSport,
    flags: FEATURE_FLAGS,
    isAdmin,
    superMode,
    hasCapability,
    hasPlanCapability       // ← P3.2: capabilities do PLANO (GET /me/context)
  });

  // Agrupar itens por seção
  const groupedItems = groupItems(sidebarItems);

  // LEGACY: Manter visibleSections para compatibilidade (será removido gradualmente)
  const visibleSections = getVisibleNavSections({
    roleKey: activeRole,
    sportKey: activeSport,
    isAdmin,
    superMode,
    hasCapability,
  });

  // Scroll Lock quando aberto no mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  // Fechar sidebar ao navegar
  useEffect(() => {
    onClose?.();
  }, [location.pathname, onClose]);

  // Handler de logout (PROMPT 3)
  const handleLogout = () => {
    if (window.confirm(lang === 'en' ? 'Are you sure you want to sign out?' : 'Tem certeza que deseja sair?')) {
      logout();
      navigate('/login');
      onClose?.(); // Fecha sidebar no mobile
    }
  };

  // LOG DE DIAGNÓSTICO (Prompt 3 - Anti-tela-branca)
  console.log('[GlobalSidebar] Rendering with:', {
    location: location.pathname,
    navConfig: true,
    ICONS: !!ICONS,
    sectionsCount: visibleSections.length,
    visibleSections: visibleSections.length,
    isAdmin
  });

  return (
    <>
      {/* Mobile Overlay */}
      <div 
        className="fixed inset-0 bg-black transition-opacity duration-300 lg:hidden" 
        style={{
          zIndex: 40,
          opacity: isOpen ? 0.4 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          touchAction: 'none'
        }}
        onClick={onClose}
      />

      {/* Sidebar Container */}
      <aside 
        className={`
          fixed top-0 left-0 h-screen w-72 
          bg-white border-r border-gray-200 
          flex flex-col overflow-hidden
          transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          z-40 shadow-2xl lg:shadow-none
        `}
      >
        <div className="flex-1 overflow-y-auto min-h-0 overscroll-contain">
          
          {/* 1. LOGO — VUKZ (post-login) */}
          <div className="px-4 py-5 border-b border-gray-100 flex items-center justify-center">
            <img 
              src="/vukz-logo.jpeg" 
              alt="VUKZ" 
              style={{ height: '72px' }}
              className="w-auto object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                const placeholder = document.createElement('div');
                placeholder.className = 'rounded-xl flex items-center justify-center font-black text-white text-2xl';
                placeholder.style.cssText = 'width:72px;height:72px;background:#111111';
                placeholder.innerHTML = 'V';
                e.target.parentNode.appendChild(placeholder);
              }}
            />
          </div>

          {/* 2. BUSCA GLOBAL */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <input
                type="text"
                placeholder={lang === 'en' ? 'Search people, teams...' : 'Buscar pessoas, times...'}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400/20 focus:border-gray-500 text-sm bg-gray-50/50"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                {ICONS.search && <ICONS.search />}
              </div>
            </div>
          </div>

          {/* 2.5 SELETORES (colapsável) — hidden for single-role/single-sport non-admin */}
          {showSelectorsSection && (
          <div className="border-b border-gray-100">
            <button
              onClick={toggleSelectors}
              className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 transition-colors duration-200"
            >
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: BRAND_GOLD }}>
                {selectorsHidden
                ? (lang === 'en' ? 'Show selectors' : 'Mostrar seletores')
                : (lang === 'en' ? 'Hide selectors' : 'Ocultar seletores')}
              </span>
              <svg
                className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${selectorsHidden ? '' : 'rotate-180'}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {!selectorsHidden && (
              <div>
                <SportSelector />
                <RoleSwitcher />
                <TeamAthleteSelector />
                <SuperModeToggle />
              </div>
            )}
          </div>
          )}

          {/* 3. NAVEGACAO DINAMICA - Consumindo SIDEBAR_ITEMS via sidebarConfig.js */}
          {GROUP_ORDER.map((groupKey, groupIdx) => {
            const items = groupedItems[groupKey] || [];
            if (items.length === 0) return null;
            
            // For non-admin: skip group if ALL items are disabled ("Em breve")
            const hasVisibleItems = isSuperAdmin
              ? true
              : items.some(item => isItemEnabled(item, FEATURE_FLAGS, isSuperAdmin, hasCapability));
            if (!hasVisibleItems) return null;
            
            const isCollapsible = COLLAPSIBLE_GROUPS.includes(groupKey);
            const isCollapsed = isCollapsible && !!collapsedSections[groupKey];
            const groupTitle = (lang === 'en' ? GROUP_TITLES_EN[groupKey] : GROUP_TITLES[groupKey]) || groupKey;
            const groupIconName = GROUP_ICONS[groupKey];
            const GroupIconComponent = groupIconName ? ICONS[groupIconName] : null;
            // Rotas podem carregar querystring de aterrissagem (ex: /treinos?tab=sessions);
            // o match compara só o pathname, senão o item nunca fica ativo.
            const hasActiveChild = items.some(item => item.route && location.pathname.startsWith(item.route.split('?')[0]));

            return (
            <div 
              key={groupKey}
              className={`px-3 pt-4 pb-2 ${groupIdx > 0 ? 'border-t border-gray-100' : ''}`}
            >
              {/* Titulo da Secao (colapsavel ou normal) */}
              {groupTitle && isCollapsible ? (
                <button
                  onClick={() => setCollapsedSections(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                    hasActiveChild ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                >
                  {GroupIconComponent && <GroupIconComponent className="w-4 h-4" style={{ color: BRAND_GREEN }} />}
                  <span className="text-sm font-bold" style={{ color: BRAND_GREEN }}>
                    {groupTitle}
                  </span>
                  <svg
                    className={`w-3.5 h-3.5 ml-auto text-gray-400 transition-transform duration-200 ${!isCollapsed ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              ) : groupTitle ? (
                <h4 className="px-3 text-[10px] font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: BRAND_GREEN, opacity: 0.6 }}>
                  {GroupIconComponent && <GroupIconComponent className="w-3.5 h-3.5" />}
                  {groupTitle}
                </h4>
              ) : null}
              
              {/* Itens da Secao (ocultos se colapsado) */}
              {(!isCollapsible || !isCollapsed) && (
              <nav className={`space-y-1 ${isCollapsible ? 'mt-1 ml-2 pl-2 border-l-2 border-gray-100' : ''}`}>
                {items.map((item) => {
                  // Match por pathname (query da rota ignorada) + "rota mais longa
                  // vence": em /treinos/novo, só o item /treinos/novo acende, não
                  // o /treinos junto. ATENÇÃO: a resolução é INTRA-GRUPO (`items`
                  // é a lista do grupo corrente, não o config inteiro) — dois itens
                  // com mesmo pathname-base em grupos diferentes acenderiam ambos.
                  // NOTA: o campo `match` do sidebarConfig não é
                  // lido aqui — startsWith é hardcoded desde sempre.
                  const itemBase = item.route ? item.route.split('?')[0] : null;
                  const isActive = itemBase && location.pathname.startsWith(itemBase)
                    && !items.some(other => {
                      const otherBase = other.route ? other.route.split('?')[0] : null;
                      return otherBase && otherBase.length > itemBase.length && location.pathname.startsWith(otherBase);
                    });
                  const IconComponent = item.icon ? ICONS[item.icon] : null;
                  const enabled = isItemEnabled(item, FEATURE_FLAGS, isSuperAdmin, hasCapability);
                  
                  // "Em breve" items: only superadmin sees them (as preview).
                  // Regular users only see what's actually active.
                  if (!enabled) {
                    if (!isSuperAdmin) return null;
                    return (
                      <div
                        key={item.id}
                        className="relative flex items-center gap-3 px-3 py-2.5 rounded-lg opacity-50 cursor-not-allowed"
                        title="Em breve"
                      >
                        {IconComponent && (
                          <div style={{ color: BRAND_GREEN }} className="opacity-50">
                            <IconComponent />
                          </div>
                        )}
                        <span className="text-sm" style={{ color: BRAND_GREEN }}>
                          {getLabelForLang(item, lang)}
                        </span>
                        <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-300 text-gray-600">
                          {lang === 'en' ? 'Soon' : 'Em breve'}
                        </span>
                      </div>
                    );
                  }
                  
                  // Itens ativos
                  return (
                    <Link
                      key={item.id}
                      to={item.route}
                      onClick={onClose}
                      className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                        isActive 
                          ? 'bg-gray-100 font-bold' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Left-Border Active State */}
                      {isActive && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-r-sm" style={{ backgroundColor: BRAND_GREEN }} />
                      )}
                      
                      {/* Icone */}
                      {IconComponent && (
                        <div style={{ color: BRAND_GREEN }} className={`${isActive ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`}>
                          <IconComponent />
                        </div>
                      )}
                      
                      {/* Label */}
                      <span className="text-sm" style={{ color: BRAND_GREEN }}>
                        {getLabelForLang(item, lang)}
                      </span>
                      
                      {/* Badge (NEW, BETA, AI, etc) */}
                      {item.badge && item.badge !== 'Em breve' && item.badge !== 'Soon' && (
                        <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          item.badge === 'NEW' ? 'bg-green-500 text-white' :
                          item.badge === 'BETA' ? 'bg-blue-500 text-white' :
                          item.badge === 'AI' ? 'bg-purple-500 text-white' :
                          item.badge === 'FOUNDER' ? 'text-white' : 'bg-gray-200 text-gray-700'
                        }`} style={item.badge === 'FOUNDER' ? { backgroundColor: BRAND_GOLD } : {}}>
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </nav>
              )}
            </div>
            );
          })}

          {/* 4. RODAPÉ INSTITUCIONAL (rola junto com a navegação) */}
          <div className="mt-4 px-4 pb-6 pt-4 border-t border-gray-100">
            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">
              {lang === 'en' ? 'Institutional Links' : 'Links Institucionais'}
            </h4>
            <div className="space-y-2">
              <Link to="/sobre" className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors">
                {ICONS.info && <ICONS.info />} {lang === 'en' ? 'About' : 'Sobre'}
              </Link>
              <Link to="/privacidade" className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors">
                {ICONS.lock && <ICONS.lock />} {lang === 'en' ? 'Privacy' : 'Privacidade'}
              </Link>
              <Link to="/termos" className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors">
                {ICONS.fileText && <ICONS.fileText />} {lang === 'en' ? 'Terms of Use' : 'Termos de Uso'}
              </Link>
              <Link to="/contato" className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors">
                {ICONS.mail && <ICONS.mail />} {lang === 'en' ? 'Support & Contact' : 'Suporte & Contato'}
              </Link>
            </div>

            {/* Microsoft for Startups */}
            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">{lang === 'en' ? 'Built with support from' : 'Construído com apoio de'}</p>
              <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                <img 
                  src="/microsoft-logo.png" 
                  alt="Microsoft for Startups" 
                  className="h-5 w-auto object-contain"
                  onError={(e) => e.target.style.display = 'none'}
                />
                <div>
                  <p className="text-[11px] font-bold text-gray-700">Microsoft for Startups</p>
                  <p className="text-[9px] text-gray-400">Empowering Innovation</p>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="mt-6 flex items-center gap-2 opacity-50 grayscale hover:grayscale-0 transition-all">
              <img 
                src="/vukz-logo.jpeg" 
                alt="VUKZ" 
                className="h-5"
                onError={(e) => e.target.style.display = 'none'}
              />
              <span className="text-[10px] text-gray-400 font-medium">© 2026 VUKZ</span>
            </div>
          </div>

        </div>

        {/* 5. MENU DE CONTA — TRAVADO no rodapé do sidebar (fora da área de
            scroll). Avatar + nome + clube com dropdown ascendente (Perfil/
            Meu consumo/Configurações/Ajuda/Idioma/Sair) — sempre visível,
            a navegação rola atrás. O dropdown (absolute bottom-full) abre
            para cima sobre a área de nav, dentro do próprio aside. Mesmo
            comportamento no drawer mobile (mesmo container flex-col). */}
        <div className="flex-shrink-0 border-t border-gray-100 bg-white px-3 py-2">
          <AccountMenu />
        </div>
      </aside>
    </>
  );
};

export default GlobalSidebar;
